import React, { useState, useEffect, useContext } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
// import { apiUrl } from "../../../../utils/apiUrl";
import { SecurityContext } from "../../../Base";
import { Box, CircularProgress, Divider, Paper, Alert } from "@mui/material";
import { UseMutateFunction } from "react-query";
import { Order } from "../../../../types/api/Order";
import useUser from "../../../../queries/routes/useUser";
import useCurrentCart from "../../../../queries/routes/useCurrentCart";
import { useParams, useNavigate } from "react-router-dom";
// import GenieText from "../../../../components/core/genie-text/GenieText";
// import { formatCurrency } from "../../../../utils/currency";
import { Get, Post } from "../../../../queries/utils/api";
import { useAuth0 } from "@auth0/auth0-react";
import GenieText from "../../../../components/core/genie-text/GenieText";

// Make sure to call loadStripe outside a component's render to avoid
// recreating the Stripe object on every render.
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const appearance = {
  theme: "stripe",
};

interface StripeCheckoutProps {
  submitOrder: UseMutateFunction<Order, unknown, void, unknown>;
  disabled?: boolean;
}

export default function StripeCheckout({ submitOrder, disabled = false }: StripeCheckoutProps) {
  const securityContext = useContext(SecurityContext);
  const [clientSecret, setClientSecret] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [connectedAccountId, setConnectedAccountId] = useState("");
  const { org_id } = useParams();
  const navigate = useNavigate();
  const { data: user } = useUser();
  const { data: cart } = useCurrentCart(org_id!, user?.organizations[0].id!);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (!cart || !cart.order) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const accessToken = await getAccessTokenSilently();

        // Create checkout session using Post utility
        const data = await Post<{
          clientSecret: string;
          account_id: string;
          error?: string;
        }>(
          `/api/stripe/organization/${org_id}/create-checkout-session`,
          accessToken,
          { cart }
        );

        if (data.error) {
          console.error("Error creating checkout session:", data.error);
          return;
        }
        setClientSecret(data.clientSecret);
        setConnectedAccountId(data.account_id);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently, org_id, cart]);

  if (isLoading) {
    return (
      <Box
        mt={2}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!cart || !cart.order) {
    return null;
  }

  // If disabled, show a message instead of the Stripe checkout
  if (disabled) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <GenieText fontSize="small">
            The seller needs to complete their payment setup before checkout can proceed. Please reach out to them directly for assistance.
          </GenieText>
        </Alert>
        {/* <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 3,
            bgcolor: "grey.50",
            opacity: 0.6
          }}
        >
          <GenieText fontSize="small" subdued>
            Stripe Checkout Form (Disabled)
          </GenieText>
        </Box> */}
      </Box>
    );
  }

  // Create a new stripe promise with the connected account ID
  const connectedStripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string,
    {
      stripeAccount: connectedAccountId,
    }
  );

  return (
    <div>
      {clientSecret && connectedAccountId && (
        <EmbeddedCheckoutProvider
          stripe={connectedStripePromise}
          options={{
            clientSecret,
            onComplete: () => {
              // Redirect to the return URL with the session ID
              navigate(
                `/storefront/${org_id}/checkout/return?session_id=${
                  clientSecret.split("_secret_")[0]
                }`
              );
            },
          }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      )}
    </div>
  );
}
